import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { EnvironmentThemeProvider } from '@codex-brightworld/themes';
import { formatPrice, resolveByCountry } from '@codex/helpers';
import { List } from '@codex/basics';
import { Icon } from '@codex-brightworld/icons';

const WrapperProductInfo = styled.div`
  @font-face {
    font-family: 'CurrysSans';
    src: url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.woff') format('woff'),
        url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.otf') format('truetype'),
        url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.eot?') format('embedded-opentype');
  }

  background: ${(props) => props.theme.colors.neutral.white};
  font-family: CurrysSans;
`;

const Tooltip = styled.div`
  margin-bottom: 0;
  font-size: 12px;
  border: 1px solid ${(props) => props.theme.colors.neutral.grey[2]};
  background-color: ${(props) => props.theme.colors.neutral.white};
  text-align: left;
  border-radius: 10px;
`;

const HeaderMessageAdded = styled.div`
  font-size: 18px;
  font-weight: 400;
  line-height: 17px;

  > span {
    font-weight: 700;
  }
`;

const HeaderProductInfo = styled.div`
  display: flex;
  padding: 20px 24px 21px 13px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 20px 18px 16px 13px;
  }
`;

const HeaderImage = styled.div`
  flex-basis: 72px;
  height: 64px;
  margin-right: 19px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    flex-basis: 40px;
    height: 36px;
    margin-right: 14px;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

const StyledProductName = styled.span`
  font-size: 16px;
  line-height: 17px;
  padding-top: 10px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    line-height: 15px;
  }
`;

const StyledProductPrice = styled.span`
  font-size: 16px;
  font-weight: bold;
  padding-top: 10px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    font-size: 15px;
    line-height: 14px;
  }
`;

const StyledHeaderMessage = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 0;
  color: #000;
    
  > strong {
    font-size: 16px;
  }
`;

const StyledShowItems = styled.span`
  float: right;
  
  &:hover {
    cursor: pointer;
  }
`;

const StyledLastLine = styled.span`
  margin-top: 10px;
`;

const StyledChevron = styled(Icon)`
  margin: 0;
  width: 22px;
  height: 10px;
  margin-right: ${(props) => (props.isToggled ? '11px' : '-1px')};
  margin-top: -2px;

  svg path:last-child {
    stroke: none;
    fill: ${(props) => props.theme.colors.primary.purple};
  }

  @media ${(props) => props.theme.breakpoints.mobile} {
    width: 20px;
    height: 10px;
    margin-right: ${(props) => (props.isToggled ? '5px' : '-5px')};
    margin-top: -3px;
  }
  
  > svg {
    > g {
      /* Hide border of the icon */
      path:nth-child(1) {
        display: none
      }
    }
  }
`;

const StyledChevronText = styled.span`
  font-family: CurrysSans;
  font-size: 14px;
  color: ${(props) => props.theme.colors.primary.purple};

  @media ${(props) => props.theme.breakpoints.mobile} {
    margin-left: 1px;
    font-size: 13px;
  }
`;

const StyledMoreDetails = styled.div`
  padding: 0px 15px 0px 15px;
  
  transition: max-height .3s linear, border-top .3s cubic-bezier(1,0,0,1);
  overflow: hidden;
  max-height: ${(props) => (
    props.isToggled ? `${props.itemsLength * 80}px` : '0px'
  )};      
  border-top: ${(props) => (
    props.isToggled ? `1px solid ${(props) => props.theme.colors.neutral.grey[2]}` : '0px'
  )};            
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 0px 10px 0px 10px;
  }  
`;

const StyledListText = styled.span`
  font-family: CurrysSans;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.shades[1]};
  margin-left: 8px;

  @media ${(props) => props.theme.breakpoints.mobile} {
    font-size: 12px;
  }
`;

const StyledListIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.buy.default};
  && {
    width: 28px;
    margin: 0;
  }
  
  > svg {
    > g {
      /* Hide border of the icon */
      path:nth-child(1) {
        display: none
      }
    }
  }
`;

const StyledBundleItem = styled.div`
  padding: 12px 0 10px 0;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 8px 0 7px 0;
  }
`;

const StyledLine = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.neutral.grey[2]};
  margin: 0;
`;

const ProductInfo = ({
  productTitle, productPrice, productImgUrl, translation, bundle
}) => {
  const { items, isExpanded } = bundle;
  const [isToggled, setToggled] = useState(isExpanded);

  let bundleItemsElement = null;
  if (items.length) {
    bundleItemsElement = (
      <>
        {isToggled && <StyledLine />}
        <StyledMoreDetails isToggled={isToggled} itemsLength={items.length}>
          <List items={items}>
            {(item, index, ItemWrapper) => (
              <React.Fragment key={index}>
                {index > 0 && <StyledLine />}
                <StyledBundleItem>
                  <ItemWrapper key={index}>
                    <StyledListIcon icon={'plus'} />
                    <StyledListText>{item}</StyledListText>
                  </ItemWrapper>
                </StyledBundleItem>
              </React.Fragment>
            )}
          </List>
        </StyledMoreDetails>
      </>
    );
  }

  let bundleController = null;
  if (items.length) {
    bundleController = (
      <StyledShowItems onClick={() => setToggled(!isToggled)}>
        <StyledChevron
          icon={'arrowright'}
          rotate={isToggled ? 270 : 90}
          isToggled={isToggled}
        />
        <StyledChevronText>
          {isToggled ? 'Hide' : 'Show'}
          {' '}
          items
        </StyledChevronText>
      </StyledShowItems>
    );
  }

  return (
    <EnvironmentThemeProvider>
      <WrapperProductInfo>
        <Tooltip>
          <HeaderProductInfo>
            <HeaderImage>
              <img src={productImgUrl} alt={productTitle} />
            </HeaderImage>
            <StyledHeaderMessage>
              <HeaderMessageAdded>
                {translation}
              </HeaderMessageAdded>
              <StyledProductName>{productTitle}</StyledProductName>
              <StyledLastLine>
                {productPrice && (
                  <StyledProductPrice>
                    {formatPrice(productPrice, false, resolveByCountry('GBP', 'EUR'))}
                  </StyledProductPrice>
                )}
                {bundleController}
              </StyledLastLine>
            </StyledHeaderMessage>
          </HeaderProductInfo>
          {bundleItemsElement}
        </Tooltip>
      </WrapperProductInfo>
    </EnvironmentThemeProvider>
  );
};

ProductInfo.propTypes = {
  productTitle: PropTypes.string.isRequired,
  productPrice: PropTypes.number,
  productImgUrl: PropTypes.string.isRequired,
  translation: PropTypes.string,
  bundle: PropTypes.shape({
    isExpanded: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.string).isRequired
  })
};

ProductInfo.defaultProps = {
  productPrice: null,
  translation: 'This item has been added to your basket',
  bundle: {
    isExpanded: false,
    items: []
  }
};

export default ProductInfo;
